import { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2, Star, Loader2, ShieldCheck, Info } from 'lucide-react';

const BRAND = 'var(--ggj-accent)';

function Section({ title, children, action }: {
  title: React.ReactNode; children: React.ReactNode; action?: React.ReactNode;
}) {
  return (
    <div className="ggj-param-group overflow-hidden">
      <div className="ggj-profile-section-h flex items-center justify-between">
        <span className="flex items-center gap-2">{title}</span>
        {action}
      </div>
      <div className="ggj-profile-section-body">{children}</div>
    </div>
  );
}
import { useAuth } from '@core/auth/useAuth';
import { usePermissions } from '@core/auth/usePermissions';
import { maskingService } from '@core/supabase/services/maskingService';
import { MaskingTemplate } from '@core/types';
import { EmptyState } from '@shared/components/ui/EmptyState';
import { FullPageSpinner } from '@shared/components/ui/Spinner';
import { Modal } from '@shared/components/ui/Modal';
import { ConfirmDialog } from '@shared/components/ui/ConfirmDialog';

const FIELD_TOGGLES: { key: keyof MaskingTemplate; label: string }[] = [
  { key: 'mask_full_name',       label: 'Full Name' },
  { key: 'mask_email',           label: 'Email Address' },
  { key: 'mask_phone',           label: 'Phone Number' },
  { key: 'mask_linkedin_url',    label: 'LinkedIn URL' },
  { key: 'mask_current_company', label: 'Current Company' },
  { key: 'mask_location',        label: 'Location' },
];

const DEFAULT_TEMPLATE: Omit<MaskingTemplate, 'id' | 'created_at'> = {
  org_id:              '',
  name:                '',
  is_default:          false,
  mask_full_name:      true,
  mask_email:          true,
  mask_phone:          true,
  mask_linkedin_url:   true,
  mask_current_company: false,
  mask_location:       false,
  name_replacement:    'Candidate',
  watermark_visible:   true,
  watermark_text:      'Shared exclusively via {{ORG_NAME}} — Ref: {{REF_ID}}',
};

export function MaskingTemplatesPage() {
  const { user } = useAuth();
  const perms = usePermissions();
  const [templates, setTemplates] = useState<MaskingTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editTarget, setEditTarget] = useState<MaskingTemplate | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<MaskingTemplate | null>(null);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [form, setForm] = useState<Omit<MaskingTemplate, 'id' | 'created_at'>>(DEFAULT_TEMPLATE);

  useEffect(() => {
    if (!user?.orgId) return;
    maskingService.listTemplates(user.orgId).then(setTemplates).finally(() => setLoading(false));
  }, [user?.orgId]);

  const openNew = () => {
    setEditTarget(null);
    setForm({ ...DEFAULT_TEMPLATE, org_id: user?.orgId ?? '' });
    setShowModal(true);
  };

  const openEdit = (t: MaskingTemplate) => {
    setEditTarget(t);
    const { id: _id, created_at: _ca, ...rest } = t;
    setForm(rest);
    setShowModal(true);
  };

  const toggle = (key: keyof MaskingTemplate) =>
    setForm((f) => ({ ...f, [key]: !f[key as keyof typeof f] }));

  const handleSave = async () => {
    if (!form.name.trim() || !user?.orgId) return;
    setSaving(true);
    try {
      if (editTarget) {
        await maskingService.updateTemplate(editTarget.id, form);
        setTemplates((prev) => prev.map((t) => t.id === editTarget.id ? { ...t, ...form } : t));
      } else {
        const created = await maskingService.createTemplate({ ...form, org_id: user.orgId });
        setTemplates((prev) => [...prev, created]);
      }
      setShowModal(false);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    await maskingService.deleteTemplate(deleteTarget.id);
    setTemplates((prev) => prev.filter((t) => t.id !== deleteTarget.id));
    setDeleteTarget(null);
    setDeleting(false);
  };

  const handleSetDefault = async (t: MaskingTemplate) => {
    if (!user?.orgId) return;
    await maskingService.setDefault(user.orgId, t.id);
    setTemplates((prev) => prev.map((x) => ({ ...x, is_default: x.id === t.id })));
  };

  return (
    <div className="space-y-4">
      <div className="mb-1">
        <h2 className="text-[15px] font-semibold">Masking</h2>
        <p className="text-[12px] vscode-text-tertiary mt-0.5">Control which candidate fields are hidden when sharing profiles with clients.</p>
      </div>

      {/* ── About ── */}
      <Section
        title={<span className="flex items-center gap-2"><Info className="w-3.5 h-3.5" style={{ color: BRAND }} /> About Masking Templates</span>}
      >
        <p className="ggj-text-meta vscode-text-secondary leading-relaxed mb-3">
          Masking templates control which candidate fields are hidden when generating shareable profiles for clients.
          Set a <strong className="vscode-text-primary">default</strong> template to apply it automatically each time you share a profile.
        </p>
        <div className="grid sm:grid-cols-2 gap-2.5">
          {[
            { tip: 'Always mask phone numbers before sharing externally.',           cls: 'text-sky-500 dark:text-sky-400' },
            { tip: 'Create sector-specific templates (e.g. BFSI vs IT roles).',     cls: 'text-[color:var(--ggj-accent)]' },
            { tip: 'Use the watermark to attribute candidate source to your firm.', cls: 'text-green-500'  },
            { tip: 'Set a default template to avoid accidental disclosure.',         cls: 'text-amber-500'  },
          ].map(({ tip, cls }) => (
            <div key={tip} className="flex items-start gap-2 rounded-[10px] border vscode-border px-3 py-2.5">
              <ShieldCheck className={`w-3.5 h-3.5 shrink-0 mt-0.5 ${cls}`} />
              <p className="ggj-text-meta vscode-text-secondary">{tip}</p>
            </div>
          ))}
        </div>
      </Section>

      {/* ── Templates list ── */}
      <Section
        title={<span className="flex items-center gap-1.5"><ShieldCheck className="w-3.5 h-3.5" style={{ color: BRAND }} /> Masking Templates</span>}
        action={perms.canManageTeam
          ? <button type="button" className="btn-primary ggj-text-meta py-1 px-3 flex items-center gap-1.5" onClick={openNew}>
              <Plus className="w-3.5 h-3.5" /> New Template
            </button>
          : undefined
        }
      >
        <div className="p-0 -mx-4 -mb-3">
          {loading ? (
            <FullPageSpinner />
          ) : templates.length === 0 ? (
            <div className="px-4 pb-3">
              <EmptyState
                icon={ShieldCheck}
                title="No masking templates"
                description="Create a template to control which candidate fields are hidden when sharing profiles."
                action={
                  <button className="btn-primary" onClick={openNew}>
                    <Plus className="w-4 h-4" /> New Template
                  </button>
                }
              />
            </div>
          ) : (
            <ul className="divide-y divide-[var(--vscode-border-primary)]/40">
              {templates.map((t) => (
                <li key={t.id} className="flex items-start justify-between gap-4 px-5 py-4 hover:vscode-bg-hover transition-colors">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1.5 flex-wrap">
                      <p className="font-semibold ggj-text-body text-black dark:text-zinc-200">{t.name}</p>
                      {t.is_default && (
                        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 ggj-text-body font-normal rounded-full bg-[color:color-mix(in_srgb,var(--ggj-accent)_15%,transparent)] text-[color:var(--ggj-accent)] dark:text-[color:color-mix(in_srgb,var(--ggj-accent)_75%,white)] border border-[color:color-mix(in_srgb,var(--ggj-accent)_20%,transparent)]">
                          <Star className="w-2.5 h-2.5" /> Default
                        </span>
                      )}
                    </div>
                    <div className="flex flex-wrap gap-1.5 mt-1">
                      {FIELD_TOGGLES.map(({ key, label }) =>
                        t[key] ? (
                          <span
                            key={key}
                            className="px-2 py-0.5 ggj-text-body font-normal rounded-full bg-slate-500/12 text-slate-600 dark:text-slate-300 border border-slate-500/25"
                          >
                            Hidden: {label}
                          </span>
                        ) : null
                      )}
                      {t.watermark_visible && (
                        <span className="px-2 py-0.5 ggj-text-body font-normal rounded-full vscode-bg-tertiary vscode-text-secondary border vscode-border">
                          Watermark
                        </span>
                      )}
                      {FIELD_TOGGLES.every(({ key }) => !t[key]) && !t.watermark_visible && (
                        <span className="text-[10px] vscode-text-tertiary italic">No fields masked</span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    {!t.is_default && (
                      <button type="button" onClick={() => void handleSetDefault(t)}
                        className="p-1.5 vscode-text-tertiary hover:text-yellow-400 transition-colors rounded-[10px] hover:vscode-bg-hover"
                        title="Set as default">
                        <Star className="w-4 h-4" />
                      </button>
                    )}
                    <button type="button" onClick={() => openEdit(t)}
                      className="p-1.5 vscode-text-tertiary hover:vscode-text-primary transition-colors rounded-[10px] hover:vscode-bg-hover"
                      title="Edit template">
                      <Edit2 className="w-4 h-4" />
                    </button>
                    {!t.is_default && (
                      <button type="button" onClick={() => setDeleteTarget(t)}
                        className="p-1.5 vscode-text-tertiary hover:text-amber-500 transition-colors rounded-[10px] hover:vscode-bg-hover"
                        title="Delete template">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </Section>

      {/* Create/Edit Modal */}
      <Modal
        open={showModal}
        onClose={() => setShowModal(false)}
        title={editTarget ? 'Edit Template' : 'New Masking Template'}
        subtitle={
          editTarget
            ? 'Update visibility toggles and watermark for this template.'
            : 'Name the template and choose which candidate fields to hide when sharing.'
        }
        icon={<ShieldCheck className="w-4 h-4" />}
        size="md"
      >
        <div className="space-y-4">
          <div>
            <label className="label">Template Name *</label>
            <input
              className="input"
              placeholder="e.g. Standard Masking, Trusted Client"
              value={form.name}
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
            />
          </div>

          <div>
            <label className="label mb-2 block">Fields to Hide</label>
            <div className="space-y-2">
              {FIELD_TOGGLES.map(({ key, label }) => (
                <label key={key} className="flex items-center gap-3 cursor-pointer group">
                  <div
                    onClick={() => toggle(key)}
                    className={`w-9 h-5 rounded-full transition-colors cursor-pointer flex-shrink-0 ${
                      form[key as keyof typeof form]
                        ? 'bg-[color-mix(in_srgb,var(--ggj-accent)_90%,#6366f1)]'
                        : 'bg-[color-mix(in_srgb,var(--vscode-bg-tertiary)_100%,transparent)]'
                    }`}
                  >
                    <div className={`w-3.5 h-3.5 bg-white rounded-full mt-0.75 transition-transform ${
                      form[key as keyof typeof form] ? 'translate-x-4' : 'translate-x-0.5'
                    } mt-[3px] ml-[3px]`} />
                  </div>
                  <span className="ggj-text-body vscode-text-secondary group-hover:vscode-text-primary transition-colors">{label}</span>
                </label>
              ))}
            </div>
          </div>

          <div>
            <label className="label">Alias Replacement Text</label>
            <input
              className="input"
              placeholder="Candidate"
              value={form.name_replacement}
              onChange={(e) => setForm((f) => ({ ...f, name_replacement: e.target.value }))}
            />
            <p className="ggj-text-meta vscode-text-tertiary mt-1">Used when name is hidden. e.g. "Candidate" → "Candidate A"</p>
          </div>

          <label className="flex items-center gap-3 cursor-pointer">
            <input
              type="checkbox"
              className="w-4 h-4 accent-brand-500"
              checked={form.watermark_visible}
              onChange={(e) => setForm((f) => ({ ...f, watermark_visible: e.target.checked }))}
            />
            <span className="ggj-text-body vscode-text-secondary">Add visible watermark to generated PDF</span>
          </label>

          {form.watermark_visible && (
            <div>
              <label className="label">Watermark Text</label>
              <input
                className="input font-mono ggj-text-meta"
                value={form.watermark_text}
                onChange={(e) => setForm((f) => ({ ...f, watermark_text: e.target.value }))}
              />
              <p className="ggj-text-meta vscode-text-tertiary mt-1">
                Tokens: {'{{ORG_NAME}}'}, {'{{REF_ID}}'}, {'{{CLIENT_NAME}}'}
              </p>
            </div>
          )}

          <div className="flex gap-2 pt-2">
            <button className="btn-secondary flex-1" onClick={() => setShowModal(false)} disabled={saving}>Cancel</button>
            <button className="btn-primary flex-1" onClick={handleSave} disabled={saving || !form.name.trim()}>
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" /> Saving...
                </>
              ) : editTarget ? (
                'Save Changes'
              ) : (
                'Create Template'
              )}
            </button>
          </div>
        </div>
      </Modal>

      <ConfirmDialog
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
        title="Delete Template"
        message={`Delete "${deleteTarget?.name}"? This cannot be undone.`}
        loading={deleting}
        requireTypedConfirm={deleteTarget?.name}
      />
    </div>
  );
}
